import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Flex, Popover } from "antd";
import { useSocketDataEffect } from "../../hooks/useSocketData";
import { setNotification } from "../../store/trailerArrivalSlice";
import closePng from "../../assets/icons/close.png";
import {
  clearLocalStorageItem,
  formatSeconds,
  getLocalStorageItem,
  setLocalStorageItem
} from "../../util/helper/helperFunctions";
import "../../assets/css/TrailerArrivalNotification/TrailerArrivalNotification.css";
import TruckIcon from "./DynamicTruckIcon";
import { DB_ERROR, SOCKET_CHANNELS } from "../../util/helper/constants";
import { useParams } from "react-router-dom";

const NotificationTransformer = ({ rawText = "", elapsedTime = "" }) => {
  const parts = rawText.split(".");
  const door = Number(parts[1]);
  const convertedElapsedTIme = elapsedTime && formatSeconds(elapsedTime);
  if (parts.length !== 2 || parts[0].toLowerCase() !== "stage" || isNaN(door)) {
    return <p className="message">{rawText}</p>;
  }

  return (
    <p className="message">
      DD <b>{door}</b>
      {convertedElapsedTIme && <span> | {convertedElapsedTIme}</span>}
    </p>
  );
};

const RenderAllNotifications = ({ notifications }) => {
  if (!notifications.length) {
    return <p className="no-message">No Messages</p>;
  }
  return (
    <Flex vertical className="notification-popover">
      {notifications.map(({ dockDoor, elapsedTime }) => (
        <NotificationTransformer rawText={dockDoor} elapsedTime={elapsedTime} />
      ))}
    </Flex>
  );
};

const TrailerArrivalNotification = ({ socket }) => {
  const dispatch = useDispatch();
  const { plantCode } = useParams();
  const localStorageKey = `${plantCode}-lastReadTrailerNotification`;
  const { data: notifications } = useSelector((state) => state.trailerArrival);

  useSocketDataEffect({
    socket,
    channel: SOCKET_CHANNELS.TRAILER_ARRIVAL,
    onData: (data) => {
      dispatch(setNotification(data));
    },
    validate: (data) => {
      if (data !== DB_ERROR && Array.isArray(data)) {
        return true;
      }
      return false;
    }
  });

  const [notify, setNotify] = useState(false);
  const [lastReadMessages, setLastReadMessages] = useState(
    getLocalStorageItem(localStorageKey) || null
  );
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  const handleClick = () => {
    // toggling the show
    setShowAllNotifications((prev) => !prev);

    /*
    after opening and closing the popover it caches the last read 
    */

    if (showAllNotifications && notifications.length) {
      const dictionary = {}; //for fast lookups.

      notifications.forEach(({ dockDoor }) => {
        dictionary[dockDoor] = dockDoor;
      });

      setLocalStorageItem(localStorageKey, dictionary);
      setLastReadMessages(dictionary);
      setNotify(false);
    }
  };

  useEffect(() => {
    let isNew = false;

    if (notifications.length === 0) {
      setNotify(false);
      return;
    }

    if (lastReadMessages && !showAllNotifications) {
      //notify when new stages available when compared to last read.
      for (let i = 0; i < notifications.length; i++) {
        const { dockDoor } = notifications[i];

        if (!lastReadMessages.hasOwnProperty(dockDoor)) {
          isNew = true;
          break;
        }
      }
      setNotify(isNew);
    } else if (!lastReadMessages) {
      // on intial mount : if there is no past read and but when are there notifications to be notified
      setNotify(true);
    }
  }, [notifications]);

  //cleanup other location's cached messages
  useEffect(() => {
    for (const key of Object.keys(localStorage)) {
      if (
        key.endsWith("-lastReadTrailerNotification") &&
        key !== localStorageKey
      ) {
        clearLocalStorageItem(key);
      }
    }
  }, []);

  return (
    <div className="trailer-notification-wrapper">
      <Popover
        content={<RenderAllNotifications notifications={notifications} />}
        open={showAllNotifications}
        arrow={false}
        trigger="click"
        placement="bottomRight"
        className="custom-popover"
        popupStyle={{ zIndex: 100 }}
        onOpenChange={(visible) => {
          // prevent popover from auto closing on outside click
          if (visible !== showAllNotifications) {
            // do nothing;
          }
        }}
        getPopupContainer={(triggerNode) => triggerNode.parentElement}
      >
        <button
          style={{
            backgroundColor:
              notify && !showAllNotifications ? "#BF2E3A" : "#FFFFFF",
            border:
              !notify || showAllNotifications ? "1px solid #4461F2" : "none"
          }}
          className="notification-button"
          onClick={handleClick}
        >
          {showAllNotifications ? (
            <img src={closePng} alt="X" height="10" width="10" />
          ) : (
            <TruckIcon
              className="truck-icon"
              color={notify ? "#ffffff" : " #4461F2"}
            />
          )}
        </button>
      </Popover>
    </div>
  );
};

export default TrailerArrivalNotification;
